import { useState, useCallback, useRef } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { useGame } from '../state/store'
import { fmt } from '../engine/decimal'
import { tapValue } from '../engine/formulas'
import { LETTERS_BY_ID } from '../config/letters'

interface FloatingInk {
  id: number
  x: number
  y: number
  value: string
}

interface Splat {
  id: number
  x: number
  y: number
}

let nextId = 0

export function TapPad() {
  const tap = useGame((s) => s.tap)
  const letters = useGame((s) => s.letters)
  const taps = useGame((s) => s.totalTaps)
  const distinct = useGame((s) => s.distinct())
  const edition = useGame((s) => s.edition)
  const perTap = tapValue(letters)
  const owned = letters.filter((l) => l.level > 0)

  const [floaters, setFloaters] = useState<FloatingInk[]>([])
  const [splats, setSplats] = useState<Splat[]>([])
  const padRef = useRef<HTMLDivElement>(null)

  let glyph = 'E'
  if (owned.length > 0) {
    const best = [...owned].sort((a, b) => b.level - a.level)[0]
    glyph = LETTERS_BY_ID[best.id]?.letter ?? 'E'
  }

  const phase = owned.length === 0 ? 'blank'
    : edition >= 1 ? 'press'
    : distinct >= 4 ? 'inked'
    : 'type'

  const motif = {
    blank: '◌',
    type: glyph,
    inked: `⬤${glyph}`,
    press: `⬤${glyph}▐`,
  }[phase]

  const label = {
    blank: 'Tap the quill',
    type: 'Tap the quill',
    inked: 'Inked type',
    press: 'Press and print',
  }[phase]

  const handleTap = useCallback((e: React.MouseEvent) => {
    tap()

    // Floating +Ink number
    const rect = (e.currentTarget as HTMLElement).getBoundingClientRect()
    const x = e.clientX - rect.left + (Math.random() - 0.5) * 40
    const y = e.clientY - rect.top - 10
    const id = nextId++
    setFloaters((prev) => [...prev, { id, x, y, value: `+${fmt(perTap)}` }])
    setTimeout(() => setFloaters((prev) => prev.filter((f) => f.id !== id)), 800)

    // Ink splat particles (3-5 small circles)
    const splatCount = 3 + Math.floor(Math.random() * 3)
    const newSplats: Splat[] = []
    for (let i = 0; i < splatCount; i++) {
      newSplats.push({
        id: nextId++,
        x: x + (Math.random() - 0.5) * 30,
        y: y + Math.random() * 20,
      })
    }
    setSplats((prev) => [...prev, ...newSplats])
    setTimeout(() => {
      setSplats((prev) => prev.filter((s) => !newSplats.find((ns) => ns.id === s.id)))
    }, 600)
  }, [tap, perTap])

  return (
    <div ref={padRef} className="relative rounded-lg border border-ink-border bg-ink-panel p-4 text-center">
      <motion.button
        whileTap={{ scale: 0.92, y: 2 }}
        onClick={handleTap}
        className="chip mx-auto h-28 w-28 select-none border-ink-tealdim bg-ink-bg text-5xl text-ink-teal shadow-glow active:shadow-none"
      >
        [{motif}]
      </motion.button>

      {/* Floating +Ink numbers */}
      <AnimatePresence>
        {floaters.map((f) => (
          <motion.div
            key={f.id}
            initial={{ opacity: 1, y: f.y, x: f.x }}
            animate={{ opacity: 0, y: f.y - 50 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.8, ease: 'easeOut' }}
            className="pointer-events-none absolute text-sm font-bold text-ink-teal"
            style={{ left: 0, top: 0 }}
          >
            {f.value}
          </motion.div>
        ))}
      </AnimatePresence>

      {/* Ink splat particles */}
      <AnimatePresence>
        {splats.map((s) => (
          <motion.div
            key={s.id}
            initial={{ opacity: 0.8, scale: 1 }}
            animate={{ opacity: 0, scale: 0.3 }}
            transition={{ duration: 0.6 }}
            className="pointer-events-none absolute h-1.5 w-1.5 rounded-full bg-ink-teal"
            style={{ left: s.x, top: s.y }}
          />
        ))}
      </AnimatePresence>

      <div className="mt-3 text-xs text-ink-muted">
        {label} · <span className="text-ink-text">+{fmt(perTap)}</span> Ink
      </div>
      <div className="text-[10px] text-ink-muted">{taps} taps</div>
    </div>
  )
}
